import { Badge, Button, Modal, Text, Group } from '@mantine/core';
import { IconBellRinging, IconLanguage, IconLogout, IconMailFilled, IconUserFilled } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import colors from '../utils/colors';
import { staggerContainer, fadeInFromTop, fadeInFromLeft, fadeIn, bounce, fadeInFromBottom } from '../utils/animations';
import { showInfoNotification, showSuccessNotification } from '../components/notifications';
import { useProfile } from '../hooks/useProfile';
import { useLoadingOverlay } from '../components/LoadingOverlayContext';
import { logout } from '../utils/auth';
import packageJson from '../../package.json';
import { Locale, useLanguage } from '../context/LanguageContext';

const Settings: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useProfile();
  const { setLoading } = useLoadingOverlay();
  const { locale, setLocale, translate } = useLanguage();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  useEffect(() => {
    setLoading(isLoading);
    
    if (!isLoading && data) {
      setTimeout(() => setShouldAnimate(true), 50);
    }
  }, [isLoading, data, setLoading]);

  const handleLogout = () => {
    logout()
    showSuccessNotification(translate('successfullyLoggedOut'), translate('success'))
  };

  const handleRequestNotifications = () => {
    if (Notification.permission === 'denied') {
      showInfoNotification(translate('notificationDenied'), translate('information'), 7000)
    } 
    else if(Notification.permission === 'granted') {
      showInfoNotification(translate('notificationAlreadyAllowed'), translate('information'), 3000)
    } 
    else {
      Notification.requestPermission().then(permission => {
        console.log("Permission:", permission);
      });
    }
  };

  const handleLanguageChange = (newLocale: Locale) => {
    setLocale(newLocale);
  };

  return (
    <motion.div 
      className="p-4 mt-6"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
      style={{ willChange: 'transform' }}
      layoutRoot
    >
      <motion.div variants={fadeInFromTop} layout>
        <p className="text-lg font-medium text-custom36p flex items-center gap-1 mb-1">
          <IconUserFilled/><span className="text-custom36s">{data?.name} {data?.surname}</span>
        </p>
      </motion.div>
      
      <motion.div variants={fadeInFromTop} layout>
        <p className="text-lg font-medium text-custom36p flex items-center gap-1 mb-1">
          <IconMailFilled/><span className="text-custom36s">{data?.email}</span>
        </p>
      </motion.div>
      
      <motion.div variants={fadeInFromTop} layout>
        <Group gap={5} mb={15} mt={10}>
          <Badge 
            size="lg" 
            fw={700}
            radius="md" 
            color={locale === 'pl-PL' ? colors.custom36p : 'gray'}
            style={{ cursor: 'pointer' }}
            onClick={() => handleLanguageChange('pl-PL')}
          >
            POLSKI
          </Badge>
          <Badge 
            size="lg" 
            fw={700}
            radius="md" 
            color={locale === 'en-EN' ? colors.custom36p : 'gray'}
            style={{ cursor: 'pointer' }} 
            onClick={() => handleLanguageChange('en-EN')}
          >
            ENGLISH
          </Badge>
          <Badge 
            size="lg" 
            fw={700}
            radius="md" 
            color={locale === 'de-DE' ? colors.custom36p : 'gray'}
            style={{ cursor: 'pointer' }} 
            onClick={() => handleLanguageChange('de-DE')}
          >
            DEUTSCH
          </Badge>
        </Group>
      </motion.div>

      <motion.div 
        className="flex flex-col items-start justify-center gap-4 mt-4"
        layout
      > 
        <motion.button
            className="text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-medium rounded-lg border-none text-md px-4 py-1.5 text-center tracking-wide"
            onClick={() => handleRequestNotifications()}
            variants={fadeInFromTop}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center"> 
              <IconBellRinging className="mr-2" />
              {translate('allowNotifications')}
            </div>
        </motion.button>
        <motion.button
            className="text-white bg-gradient-to-br from-red-800 via-red-500 to-red-800 hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-red-800 shadow-lg shadow-red-800 font-medium rounded-lg border-none text-md px-4 py-1.5 text-center tracking-wide"
            onClick={() => setOpenLogoutModal(true)}
            variants={fadeInFromTop}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center"> 
            <IconLogout className="mr-2" />
              {translate('logout')}
            </div>
        </motion.button>
        <motion.p 
          className="text-sm font-medium text-custom36p flex text-center mt-5"
          variants={fadeInFromTop}
        >
          {translate('appInfo', { version: packageJson.version })}
        </motion.p>
      </motion.div>

      <Modal opened={openLogoutModal} onClose={() => setOpenLogoutModal(false)} centered withCloseButton={false} size="lg" yOffset="1vh" radius={'lg'}
          transitionProps={{ 
            transition: 'fade', 
            duration: 100, 
            timingFunction: 'linear' 
          }} 
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
          styles={{body: {background: colors.customBg3}}}
          >
        <Text c={colors.custom36s} ta="center" fw={700} size='lg'>
          {translate('logoutConfirmation')}
        </Text>
        <Text c={colors.red} ta="center" fw={500} size='md' mt={'xs'} mb={'md'}>
          {translate('logoutWarning')}
        </Text>
        <div className="flex items-center justify-center gap-10">
          <motion.button
            className="text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p font-medium rounded-lg border-none text-md px-4 py-1.5 text-center tracking-wide"
            onClick={() => setOpenLogoutModal(false)}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center"> 
              {translate('cancel')}
            </div>
          </motion.button>
          <motion.button
            className="text-white bg-gradient-to-br from-red-800 via-red-500 to-red-800 hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-red-800 shadow-lg shadow-red-800 font-medium rounded-lg border-none text-md px-4 py-1.5 text-center tracking-wide"
            onClick={() => {
              setOpenLogoutModal(false);
              handleLogout();
            }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center"> 
              {translate('logout')}
            </div>
          </motion.button>
        </div>
      </Modal>
    </motion.div>
    
  );
};

export default Settings;
